import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Heart, Shield, Sparkles, Brain, ArrowRight } from 'lucide-react';
import therappiLogo from '@/assets/therappi-text-logo.svg';
import { supabase } from '@/integrations/supabase/client';
import { logFlight } from '@/hooks/useOnboardingStateMachine';

interface WelcomeStepProps {
  onContinue: () => void;
}

/**
 * WelcomeStep - The 4-box welcome screen (alternative to PreAuthWelcome)
 * 
 * CRITICAL SAFETY GUARD: This component performs its own session check on mount.
 * If ANY authenticated session exists, it returns null and never renders.
 */
export default function WelcomeStep({ onContinue }: WelcomeStepProps) {
  const [isBlocked, setIsBlocked] = useState<boolean | null>(null);
  
  // CRITICAL: Hard session check on mount - independent of state machine
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          logFlight('WELCOME_STEP_BLOCKED_POST_AUTH', { 
            userId: session.user.id,
            email: session.user.email 
          });
          console.error('[WelcomeStep] BLOCKED: Authenticated user detected. Returning null.');
          setIsBlocked(true);
        } else {
          setIsBlocked(false);
        }
      } catch (error) {
        setIsBlocked(false);
      }
    };
    checkSession();
  }, []);

  const features = [
    { icon: Brain, title: 'AI Therapy', description: 'Evidence-based therapeutic support' },
    { icon: Heart, title: 'Daily Check-Ins', description: 'Track your mood, sleep & energy' },
    { icon: Sparkles, title: 'Personalised Insights', description: 'AI-powered wellness recommendations' },
    { icon: Shield, title: 'Private & Secure', description: 'Your data stays encrypted' },
  ];

  // Play welcome sound when component mounts (only if not blocked)
  useEffect(() => {
    if (isBlocked !== false) return;
    
    const timer = setTimeout(() => {
      try {
        const popSound = new Audio('/sounds/therappi-guide-pop.wav');
        popSound.volume = 0.5;
        popSound.play().catch(() => {});
      } catch {
        // Ignore audio errors
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [isBlocked]);

  // HARD BLOCK: If session check found authenticated user, render nothing
  if (isBlocked === true) {
    return null;
  }
  
  // Show loading while checking session
  if (isBlocked === null) {
    return (
      <div className="fixed inset-0 min-h-screen flex items-center justify-center bg-[hsl(182,29%,26%)] z-50">
        <div className="h-12 w-12 border-4 border-[hsl(177,41%,76%)] border-t-[hsl(182,37%,46%)] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(182,29%,26%)] via-[hsl(182,29%,28%)] to-[hsl(177,41%,35%)]/20 flex flex-col items-center justify-center px-6 py-12">
      {/* Ambient background effects */}
      <motion.div 
        className="absolute top-1/4 -left-32 w-96 h-96 bg-[hsl(177,41%,50%)]/15 rounded-full blur-[120px]"
        animate={{ 
          scale: [1, 1.2, 1],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div 
        className="absolute bottom-1/4 -right-32 w-96 h-96 bg-[hsl(182,37%,35%)]/20 rounded-full blur-[120px]"
        animate={{ 
          scale: [1.2, 1, 1.2],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="relative z-10 max-w-lg w-full space-y-8">
        {/* Logo */}
        <motion.div 
          className="flex justify-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <img src={therappiLogo} alt="Therappi" className="h-12" />
        </motion.div>

        {/* Welcome message */}
        <motion.div 
          className="text-center space-y-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h1 className="text-3xl md:text-4xl font-light text-[hsl(30,30%,95%)] tracking-wide">
            Welcome to Your Wellness Journey
          </h1>
          <p className="text-lg text-[hsl(177,41%,76%)] font-light leading-relaxed">
            A therapeutic companion that grows with you - supporting your mental, physical and spiritual wellbeing.
          </p>
        </motion.div>

        {/* Feature highlights */}
        <motion.div 
          className="grid grid-cols-2 gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 space-y-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
              >
                <div className="p-2 w-fit rounded-lg bg-[hsl(177,41%,76%)]/20">
                  <Icon className="h-5 w-5 text-[hsl(177,41%,76%)]" />
                </div>
                <h3 className="text-sm font-medium text-[hsl(30,30%,95%)]">{feature.title}</h3>
                <p className="text-xs text-[hsl(177,41%,76%)]/80">{feature.description}</p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Continue button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
        >
          <Button
            onClick={onContinue}
            size="lg"
            variant="gradient"
            className="w-full tracking-wide transition-all duration-300 hover:scale-[1.02]"
          >
            Get Started
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </motion.div>

        {/* Privacy note */}
        <motion.p
          className="text-center text-xs text-[hsl(177,41%,76%)]/60"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.1 }}
        >
          By continuing, you agree to our Terms of Service and Privacy Policy
        </motion.p>
      </div>
    </div>
  );
}
